import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { fileUrl } from "@/lib/api";
import { LayoutDashboard, ShoppingBag, Receipt, UserRound, LogOut, Menu, X, Gift } from "lucide-react";

const links = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
  { to: "/app/marketplace", label: "Marketplace", icon: ShoppingBag, id: "nav-marketplace" },
  { to: "/app/rewards", label: "Rewards", icon: Gift, id: "nav-rewards" },
  { to: "/app/transactions", label: "Transactions", icon: Receipt, id: "nav-transactions" },
  { to: "/app/profile", label: "Profile", icon: UserRound, id: "nav-profile" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = ({ onClick }) =>
    links.map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        end={l.end}
        onClick={onClick}
        data-testid={l.id}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 mx-3 rounded-full text-sm font-medium transition-colors duration-150 ${
            isActive
              ? "bg-primary text-white shadow-[0_4px_14px_rgba(211,23,43,0.3)]"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary"
          }`
        }
      >
        <l.icon size={18} />
        {l.label}
      </NavLink>
    ));

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-border">
          <Logo />
        </div>
        <nav className="flex-1 py-4 flex flex-col">{NavItems({})}</nav>
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full overflow-hidden bg-secondary flex items-center justify-center shrink-0">
              {user?.photo_path ? (
                <img src={fileUrl(user.photo_path)} alt="" className="w-full h-full object-cover" />
              ) : (
                <UserRound size={18} className="text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 justify-center px-3 py-2.5 rounded-full text-sm font-medium border-2 border-border hover:border-primary hover:text-primary transition-colors duration-150"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-background z-30">
          <Logo size={24} />
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </header>
        {open && (
          <div className="lg:hidden border-b border-border bg-background z-20">
            <nav className="flex flex-col py-2">{NavItems({ onClick: () => setOpen(false) })}</nav>
            <button
              onClick={onLogout}
              data-testid="logout-btn-mobile"
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-primary"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        )}
        <main className="flex-1 p-5 sm:p-8 lg:p-12 max-w-6xl w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
